import XLSX, {WorkBook, WorkSheet} from 'sheetjs-style';

type Data = { [key: string]: unknown }[]

export interface IExcelExportService {
  addPage: (data: Data, title: string) => this;
  generateFile: (fileName: string) => void;
}

export class ExcelExportService implements IExcelExportService {
  private readonly workBook: WorkBook;

  constructor() {
    this.workBook = XLSX.utils.book_new();
    this.rightToLeft(this.workBook);
  }

  public addPage = (data: Data, title?: string, useWrapLines = false) => {
    const ws = XLSX.utils.json_to_sheet(title ? [{title}] : [], {skipHeader: true});
    XLSX.utils.sheet_add_json(ws, data, title ? {origin: 'A3'} : {});

    const headerCells = Object.keys(data[0]).reduce((cells, item, index) => {
      return [...cells, `${ExcelExportService.alphabet(index)}1`];
    }, [] as string[]);
    const bodyCells = ExcelExportService.excelCells(
      data.map((item, index) => (index + 1).toString())
    );
    this.styleHeaderCellsCenteredBold(headerCells, ws);
    if (useWrapLines) {
      this.styleCellsWrap(bodyCells, ws);
    }
    ws['!cols'] = this.fitToColumn(data);

    XLSX.utils.book_append_sheet(this.workBook, ws);

    return this;
  };

  public generateFile = (fileName: string) => {
    XLSX.writeFile(this.workBook, fileName);
  };

  private rightToLeft = (wb: WorkBook) => {
    if (!wb.Workbook) wb.Workbook = {};
    if (!wb.Workbook.Views) wb.Workbook.Views = [];
    if (!wb.Workbook.Views[0]) wb.Workbook.Views[0] = {};
    wb.Workbook.Views[0].RTL = true;
  };

  private fitToColumn = (data: Data) => {
    const defaultColumnWidth = 5;
    const columnWidths: {wch: number}[] = [];
    Object.keys(data[0]).forEach((key) => {
      const maxContentSize = data.reduce((acc, item) => {
        const value = item[key];
        const size = (value && value?.toString) ? value.toString().length : defaultColumnWidth;
        return Math.max(acc, size);
      }, defaultColumnWidth);
      columnWidths.push({
        wch: Math.max(
          key ? key.toString().length : maxContentSize,
          maxContentSize,
        )
      });
    });

    return columnWidths;
  };

  private styleHeaderCellsCenteredBold = (headersArray: string[], ws: WorkSheet) => {
    headersArray.map(item => {
      if (ws[item]) {
        ws[item].s = {
          font: {bold: true},
          alignment: {horizontal: 'center'}
        };
      }
    });
  };

  private styleCellsWrap = (cellsArray: string[], ws: WorkSheet) => {
    cellsArray.map(item => {
      if (ws[item]) {
        ws[item].s = {
          alignment: {wrapText: true}
        };
      }
    });
  };

  static alphabet(position: number) { //Return a string matching the column index given
    const LETTER = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
    return (position < 26) ? LETTER[position] : LETTER[position - 26];
  }

  static excelCells(rows: string[]) {
    const LETTER = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

    return LETTER.flatMap(d => rows.map(v => d + v));
  }
}

